import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import AddressSearch from "../components/AddressSearch";
import UiIcon from "../components/UiIcon";
import { Avatar } from "../components/Avatar";
import { Screen, PageHeader, Card, SectionTitle, Button, Toggle, Field, fieldStyle, K } from "../components/UiKit";

const todayISO = new Date().toISOString().slice(0, 10);

// Sélecteur d'amis en liste verticale (avatar, nom, case ronde) plutôt qu'un carrousel horizontal :
// aucun défilement latéral possible, donc plus aucune prise pour faire "balader" la page sur mobile.
function FriendList({ friends, selected, onToggle, single, search, lang }: { friends: any[]; selected: string[]; onToggle: (id: string) => void; single?: boolean; search: boolean; lang: string }) {
  const [q, setQ] = useState("");
  const shown = q.trim() ? friends.filter(f => (f.name || "").toLowerCase().includes(q.trim().toLowerCase())) : friends;
  return (
    <div>
      {search && (
        <div style={{ position: "relative", marginBottom: 10 }}>
          <span style={{ position: "absolute", left: 14, top: 14, color: K.faint }}><UiIcon name="search" size={20} /></span>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t(lang, "search")} style={{ ...fieldStyle, paddingLeft: 44, backgroundColor: K.surface, border: "1.5px solid transparent" }} />
        </div>
      )}
      <div style={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden", margin: "0 -4px", padding: "0 4px" }}>
        {shown.map(f => {
          const active = selected.includes(f.id);
          return (
            <div key={f.id} onClick={() => onToggle(f.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", cursor: "pointer", minWidth: 0 }}>
              <Avatar id={f.id} name={f.name} uri={f.avatar_url} size={42} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: K.ink }}>{f.name}</span>
              <span style={{ width: 24, height: 24, borderRadius: single ? 12 : 8, border: `2px solid ${active ? K.ink : "#CFCFCF"}`, backgroundColor: active ? K.ink : "transparent", display: "flex", alignItems: "center", justifyContent: "center", color: K.white, flexShrink: 0 }}>
                {active && <UiIcon name="check" size={14} strokeWidth={2.6} />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Module "Anniversaire" — organisation à PLUSIEURS avec un vrai mode surprise, contrairement à
// Connection (tête-à-tête). Entrée depuis le rappel J-30/J-7 (voir Notifications.tsx), qui
// fournit déjà birthday_friend_id/birthday_friend_name — sans ces params, on demande d'abord de
// choisir la personne fêtée via le même sélecteur d'amis que pour les invités.
export default function BirthdayEventCreate() {
  const navigate = useNavigate();
  const { user, lang } = useApp();
  const [searchParams] = useSearchParams();
  const paramFriendId = searchParams.get("birthday_friend_id") || "";
  const paramFriendName = searchParams.get("birthday_friend_name") || "";

  const [birthdayFriendId, setBirthdayFriendId] = useState(paramFriendId);
  const [birthdayFriendName, setBirthdayFriendName] = useState(paramFriendName);
  const [friends, setFriends] = useState<any[]>([]);
  const [loadingFriends, setLoadingFriends] = useState(!paramFriendId);
  const [title, setTitle] = useState(paramFriendName ? `Anniversaire de ${paramFriendName}` : "");
  const [description, setDescription] = useState("");
  const [locationName, setLocationName] = useState("");
  const [coords, setCoords] = useState<{ lat: number | null; lon: number | null } | null>(null);
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("19:00");
  const [isSurprise, setIsSurprise] = useState(true);
  const [selectedGuests, setSelectedGuests] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (!user?.id) return;
    api.get(`/users/${user.id}/friends`).then(d => setFriends(d?.friends || [])).catch(() => {}).finally(() => setLoadingFriends(false));
  }, [user?.id]);

  const canSubmit = !!birthdayFriendId && title.trim() !== "";
  const guestPool = isSurprise ? friends.filter(f => f.id !== birthdayFriendId) : friends;
  const toggleGuest = (id: string) => setSelectedGuests(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const pickBirthday = (id: string) => {
    const f = friends.find(x => x.id === id);
    if (!f) return;
    setBirthdayFriendId(f.id); setBirthdayFriendName(f.name); setTitle(`Anniversaire de ${f.name}`);
    setSelectedGuests(prev => prev.filter(x => x !== f.id));
  };

  const submit = () => {
    if (!canSubmit || submitting) return;
    if (eventDate && eventDate < todayISO) { setEventDate(""); return; }
    setSubmitting(true);
    api.post("/birthday-events", {
      birthday_user_id: birthdayFriendId,
      title: title.trim(),
      description: description.trim() || undefined,
      location_name: locationName || undefined,
      latitude: coords?.lat, longitude: coords?.lon,
      event_date: eventDate || undefined, event_time: eventTime,
      is_surprise: isSurprise,
      guest_ids: selectedGuests,
    }).then(data => {
      if (data?.event) navigate(`/birthday-events/${data.event.id}`);
      else alert(data?.error || t(lang, "bday_error_create"));
    }).catch(() => alert(t(lang, "error_connection"))).finally(() => setSubmitting(false));
  };

  const birthdayFriend = friends.find(f => f.id === birthdayFriendId);

  return (
    <Screen>
      <PageHeader title={t(lang, "bday_create_title")} onBack={() => navigate(-1)} />

      <SectionTitle>{t(lang, "bday_for_who")}</SectionTitle>
      {!birthdayFriendId ? (
        <Card>
          {loadingFriends ? <div style={{ fontSize: 13, color: K.faint }}>{t(lang, "msg_loading")}</div>
            : friends.length === 0 ? <div style={{ fontSize: 13, color: K.faint }}>{t(lang, "bday_no_friends")}</div>
            : <FriendList friends={friends} selected={[]} onToggle={pickBirthday} single search lang={lang} />}
        </Card>
      ) : (
        <Card style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {birthdayFriend ? <Avatar id={birthdayFriend.id} name={birthdayFriend.name} uri={birthdayFriend.avatar_url} size={44} /> : (
            <div style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: K.surface, display: "flex", alignItems: "center", justifyContent: "center" }}><UiIcon name="cake" size={22} /></div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: K.faint }}>{t(lang, "bday_for_prefix")}</div>
            <div style={{ fontSize: 15.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{birthdayFriendName}</div>
          </div>
          {!paramFriendId && (
            <button onClick={() => { setBirthdayFriendId(""); setBirthdayFriendName(""); setTitle(""); }} style={{ fontSize: 13, fontWeight: 600, color: K.ink, backgroundColor: K.surface, borderRadius: 16, height: 32, padding: "0 14px" }}>{t(lang, "bday_change")}</button>
          )}
        </Card>
      )}

      <Card>
        <Field label={t(lang, "bday_title_label")}>
          <input value={title} onChange={e => setTitle(e.target.value.slice(0, 100))} placeholder={t(lang, "bday_title_placeholder")} style={fieldStyle} />
        </Field>
        <Field label={t(lang, "bday_description_label")}>
          <textarea value={description} onChange={e => setDescription(e.target.value.slice(0, 500))} placeholder={t(lang, "bday_description_placeholder")} style={{ ...fieldStyle, height: 92, padding: "12px 14px", resize: "none", fontFamily: "inherit" }} />
        </Field>
        <div style={{ marginBottom: 14 }}>
          <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: K.muted, marginBottom: 6 }}>{t(lang, "bday_location_label")}</span>
          <AddressSearch value={locationName} onChange={(l, lat, lon) => { setLocationName(l); setCoords({ lat, lon }); }} placeholder={t(lang, "bday_location_placeholder")} showSavedAddresses inputStyle={fieldStyle} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 12 }}>
          <Field label={t(lang, "bday_date_label")}>
            <input type="date" min={todayISO} value={eventDate} onChange={e => { if (e.target.value && e.target.value < todayISO) return; setEventDate(e.target.value); }} style={fieldStyle} />
          </Field>
          <Field label={t(lang, "bday_time_label")}>
            <input type="time" value={eventTime} onChange={e => setEventTime(e.target.value)} style={fieldStyle} />
          </Field>
        </div>
      </Card>

      <Card style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: isSurprise ? K.ink : K.surface, color: isSurprise ? K.white : K.ink, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background-color .2s" }}>
          <UiIcon name="eyeOff" size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700 }}>{t(lang, "bday_surprise_mode_label")}</div>
          <div style={{ fontSize: 12.5, color: K.muted, marginTop: 2, lineHeight: 1.4 }}>
            {isSurprise ? `${birthdayFriendName || t(lang, "bday_surprise_default_person")}${t(lang, "bday_surprise_on_hint_p2")}` : t(lang, "bday_surprise_off_hint")}
          </div>
        </div>
        <Toggle value={isSurprise} onChange={setIsSurprise} />
      </Card>

      <SectionTitle hint={selectedGuests.length > 0 ? t(lang, "bday_selected_count").replace("{n}", String(selectedGuests.length)) : undefined}>{t(lang, "bday_invite_friends_label")}</SectionTitle>
      <Card>
        {loadingFriends ? <div style={{ fontSize: 13, color: K.faint }}>{t(lang, "msg_loading")}</div>
          : guestPool.length === 0 ? <div style={{ fontSize: 13, color: K.faint }}>{t(lang, "bday_no_friends")}</div>
          : <FriendList friends={guestPool} selected={selectedGuests} onToggle={toggleGuest} search={guestPool.length > 6} lang={lang} />}
      </Card>

      <div style={{ marginTop: 8 }}>
        <Button onClick={submit} disabled={!canSubmit || submitting}>{submitting ? t(lang, "bday_submitting") : t(lang, "bday_submit_create")}</Button>
      </div>
    </Screen>
  );
}
